/**
 * Single source of truth: which email identities count as "internal"
 * (test users + team) and must be excluded from product/ad analytics (#592).
 *
 * Used by:
 * - Meta CAPI (`sendCapiEvent`) and Meta Pixel → SUPPRESS (never send; Meta has
 *   no internal-user filter, so test conversions would pollute ROAS/audiences).
 * - PostHog `identify` → TAG (`is_internal` person property) so dashboards can
 *   filter; the native PostHog "internal & test users" filter additionally
 *   matches on the `email` person property.
 *
 * Pure function, no imports → safe on client and server.
 */

/** Whole domains: every address under them is internal. */
const INTERNAL_DOMAINS = [
  'mx-ats.com', // generic test-user domain
  'locari.uk', // also covers E2E plus-addresses (ai-tester+…@locari.uk)
  'locari.de',
  'locari.ai',
  'ametis-digital.com',
  'ametis-immobilien.com',
  'marivis-invest.com',
  'buildingminds.com',
];

/** Individual private addresses of team members used for testing. */
const INTERNAL_ADDRESSES = [
  'erik.laux@gmail.com',
  'ungermanns@outlook.com',
  'tobias.ungermanns@outlook.com',
  'gloubill@hotmail.com',
  'astrid.billecocq@gmail.com',
];

/**
 * Returns true if the email belongs to a test/internal identity that must be
 * excluded from tracking. Unknown/empty → false (a real anonymous prospect must
 * not be silently dropped).
 */
export function isInternalEmail(email?: string | null): boolean {
  if (!email) return false;

  const normalized = email.trim().toLowerCase();
  const atIndex = normalized.lastIndexOf('@');

  if (atIndex === -1) return false;

  const domain = normalized.slice(atIndex + 1);

  if (!domain) return false;
  if (INTERNAL_DOMAINS.includes(domain)) return true;

  return INTERNAL_ADDRESSES.includes(normalized);
}

/**
 * Domains, hinter denen AUSSCHLIESSLICH automatisierte Test-Läufe stecken
 * (e2e-Catch-all) – nie ein Mensch. Die nächtliche Prod-Journey bestätigt echte
 * Newsletter-DOIs (ai-tester+…@locari.uk).
 *
 * BEWUSST getrennt von `isInternalEmail` (Tobias, 23.07.2026): die breite
 * interne Liste (Team-Domains, private Team-Adressen) gilt NUR für
 * Statistik/Ads (PostHog-Tagging, Meta-CAPI-Suppression) – Teammitglieder
 * sollen den Newsletter ganz normal abonnieren können.
 */
const AUTOMATED_TEST_DOMAINS = ['locari.uk'];

/**
 * True nur für Adressen automatisierter Test-Läufe (e2e). Schmaler als
 * `isInternalEmail` – für Fälle, in denen ausschließlich echte Test-Automaten
 * (nicht das ganze Team) ausgenommen werden sollen. Der frühere CRM-/Brevo-Sync
 * (einziger Prod-Aufrufer) ist mit dem Brevo-Exit (2026-07-23) entfallen; der
 * Helper bleibt als kanonischer e2e-Adress-Check (siehe is-internal-email.test).
 */
export function isAutomatedTestEmail(email?: string | null): boolean {
  if (!email) return false;

  const normalized = email.trim().toLowerCase();
  const atIndex = normalized.lastIndexOf('@');

  if (atIndex === -1) return false;

  const domain = normalized.slice(atIndex + 1);

  return AUTOMATED_TEST_DOMAINS.includes(domain);
}
